/**
 * Collection product grid caching & restore.
 *
 * - Caches the rendered product grid and scroll position in sessionStorage
 *   so that when a buyer navigates back from a product page, the previously
 *   loaded items (including content blocks) and scroll position are restored.
 * - When landing directly on a paginated URL (e.g. ?page=3) with no cache,
 *   it preloads previous pages server-side and scrolls to the correct page.
 */

(() => {
  const STORAGE_KEY_PREFIX = 'collectionGridCache:';
  const STATE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  const supportsSessionStorage = (() => {
    try {
      const testKey = '__collection_grid_cache_test__';
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  })();

  if (!supportsSessionStorage) return;

  const HTML_STORAGE_SUFFIX = ':html';
  const HTML_COMPRESSION_PREFIX = '__lz__:';
  const LAST_COLLECTION_KEY = `${STORAGE_KEY_PREFIX}__lastCollectionPath`;

  const getHtmlStorageKey = (baseKey) => `${baseKey}${HTML_STORAGE_SUFFIX}`;

  const hasLzString =
    typeof window !== 'undefined' &&
    !!window.LZString &&
    typeof window.LZString.compressToUTF16 === 'function' &&
    typeof window.LZString.decompressFromUTF16 === 'function';

  const readHtmlFromStorage = (key) => {
    if (!supportsSessionStorage) return null;

    let stored;

    try {
      stored = window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }

    if (stored == null) return null;

    if (hasLzString && typeof stored === 'string' && stored.indexOf(HTML_COMPRESSION_PREFIX) === 0) {
      const compressed = stored.slice(HTML_COMPRESSION_PREFIX.length);

      try {
        const decompressed = window.LZString.decompressFromUTF16(compressed);

        if (typeof decompressed === 'string' && decompressed.length > 0) {
          return decompressed;
        }
      } catch (error) {
        // Ignore decompression errors and fall back to the raw stored value
      }
    }

    return stored;
  };

  const writeHtmlToStorage = (key, html) => {
    if (!supportsSessionStorage || typeof html !== 'string') return;

    let valueToStore = html;

    if (hasLzString) {
      try {
        const compressed = window.LZString.compressToUTF16(html);

        if (typeof compressed === 'string' && compressed.length > 0) {
          valueToStore = `${HTML_COMPRESSION_PREFIX}${compressed}`;
        }
      } catch (error) {
        // Ignore compression errors and fall back to raw HTML
      }
    }

    try {
      window.sessionStorage.setItem(key, valueToStore);
    } catch (error) {
      // Ignore storage errors
    }
  };

  const getCollectionPathFromLocation = () => {
    if (typeof window === 'undefined' || !window.location || !window.location.pathname) {
      return null;
    }

    const pathname = window.location.pathname;
    const match = pathname.match(/^\/collections\/[^/]+/);

    if (match && match[0]) {
      return match[0];
    }

    return pathname;
  };

  const clearCollectionCacheForNewCollectionIfNeeded = () => {
    if (!supportsSessionStorage) return;

    const currentCollectionPath = getCollectionPathFromLocation();
    if (!currentCollectionPath) return;

    let lastCollectionPath;

    try {
      lastCollectionPath = window.sessionStorage.getItem(LAST_COLLECTION_KEY);
    } catch (error) {
      return;
    }

    if (lastCollectionPath && lastCollectionPath !== currentCollectionPath) {
      try {
        const keysToRemove = [];

        for (let index = 0; index < window.sessionStorage.length; index += 1) {
          const key = window.sessionStorage.key(index);
          if (!key) continue;

          if (key.indexOf(STORAGE_KEY_PREFIX) === 0 && key !== LAST_COLLECTION_KEY) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach((key) => {
          try {
            window.sessionStorage.removeItem(key);
          } catch (error) {
            // Ignore individual removal errors
          }
        });
      } catch (error) {
        // Ignore cleanup errors
      }
    }

    try {
      window.sessionStorage.setItem(LAST_COLLECTION_KEY, currentCollectionPath);
    } catch (error) {
      // Ignore write errors
    }
  };

  clearCollectionCacheForNewCollectionIfNeeded();

  const isIos = (() => {
    if (typeof navigator === 'undefined') return false;
    return /iP(ad|hone|od)/.test(navigator.userAgent || '');
  })();

  const getCanonicalKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('phcursor');
    // Treat the stock availability filter as part of the baseline state,
    // not as a distinct cache key, so URLs with and without
    // ?filter.v.availability=1 share the same cached grid.
    url.searchParams.delete('filter.v.availability');

    const search = url.searchParams.toString();
    const canonical = url.pathname + (search ? `?${search}` : '');

    return `${STORAGE_KEY_PREFIX}${canonical}`;
  };

  const getNavigationType = () => {
    if (window.performance) {
      const navEntries = performance.getEntriesByType
        ? performance.getEntriesByType('navigation')
        : null;

      if (navEntries && navEntries.length > 0) {
        return navEntries[0].type; // 'navigate' | 'reload' | 'back_forward' | 'prerender'
      }

      if (performance.navigation) {
        const type = performance.navigation.type;

        if (type === 1) return 'reload';
        if (type === 2) return 'back_forward';
        return 'navigate';
      }
    }

    return 'navigate';
  };

  const updateStorageKeyFromURL = (instance) => {
    if (!instance || typeof instance.storageKey !== 'string') return;

    const newKey = getCanonicalKey();
    if (newKey === instance.storageKey) return;

    instance.storageKey = newKey;
    instance.state = null;
  };

  const getCurrentPageFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    const parsed = pageParam ? parseInt(pageParam, 10) : NaN;

    if (Number.isNaN(parsed) || parsed < 1) return 1;

    return parsed;
  };

  const buildPageURL = (pageNumber) => {
    const url = new URL(window.location.href);

    if (pageNumber === 1) {
      // For page 1, remove the page parameter entirely
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(pageNumber));
    }

    // Clean phcursor parameter for consistency
    url.searchParams.delete('phcursor');

    return url.pathname + (url.search || '');
  };

  const computeLoadedPagesFromDOM = () => {
    const grid = document.getElementById('product-grid');
    if (!grid) return new Set();

    const items = grid.querySelectorAll('.grid__item[data-paginated-index]');
    const pages = new Set();

    for (const item of items) {
      const value = item.getAttribute('data-paginated-index');
      const page = value ? parseInt(value, 10) : NaN;

      if (!Number.isNaN(page)) {
        pages.add(page);
      }
    }

    return pages;
  };

  const findFirstItemForPage = (pageNumber) => {
    const grid = document.getElementById('product-grid');
    if (!grid) return null;

    return grid.querySelector(
      `.grid__item[data-paginated-index="${pageNumber}"]:not(.card-description):not(.grid__item--loader)`
    );
  };

  const scrollToElement = (element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const offsetTop = rect.top + window.pageYOffset;

    window.scrollTo({
      top: Math.max(0, offsetTop - 20),
      left: 0,
      behavior: 'auto'
    });
  };

  const parseGridFromHTML = (htmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const container = doc.getElementById('ProductGridContainer');

    if (!container) {
      return {
        items: []
      };
    }

    const source = container.querySelector('.product-grid');

    if (!source) {
      return {
        items: []
      };
    }

    // Match sc-load-more.js selection:
    // - All real product grid items
    // - Any <content-block> elements used as PLP interruptors
    const nodeList = source.querySelectorAll(
      '.grid__item:not(.grid__item--loader):not(.card-description), content-block'
    );

    const items = [];

    for (const node of nodeList) {
      items.push(node.cloneNode(true));
    }

    return { items };
  };

  class ProductGridCacheManager {
    constructor() {
      this.storageKey = getCanonicalKey();
      this.state = null;
      this.saveTimeoutId = null;
      this.mutationObserver = null;
      this.initialized = false;

      this.handlePageHide = this.handlePageHide.bind(this);
      this.isNavigatingToProduct = false;

      this.init();
    }

    init() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;

      const navType = getNavigationType();
      const isBackNav = navType === 'back_forward';
      const fromProduct =
        typeof document.referrer === 'string' &&
        document.referrer.indexOf('/products/') !== -1;
      const storedState = this.loadState();

      const shouldRestoreFromState = !!storedState && (isBackNav || fromProduct);

      if (shouldRestoreFromState) {
        this.restoreFromState(storedState);
      } else {
        const currentPage = getCurrentPageFromURL();

        // When landing directly on a paginated URL (e.g. ?page=3) and we are
        // not coming from a product/back navigation, preload previous pages.
        if (currentPage > 1) {
          this.preloadPreviousPagesForInitialPage(currentPage);
        }
      }

      this.setupMutationObserver(container);
      this.setupProductClickHandler();
      this.setupLoadMoreListener();
      this.setupUnloadListeners();

      // Handle BFCache restoration (e.g. Safari back swipe)
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          const storedState = this.loadState();
          if (storedState) {
            this.restoreFromState(storedState);
          }
        }
      });

      // Initial save so we always have a baseline snapshot
      this.saveState();

      this.initialized = true;
    }

    loadState() {
      if (!supportsSessionStorage) return null;

      let raw;

      try {
        raw = window.sessionStorage.getItem(this.storageKey);
      } catch (error) {
        return null;
      }

      if (!raw) return null;

      let parsed;

      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        this.clearState();
        return null;
      }

      if (!parsed || typeof parsed !== 'object') {
        this.clearState();
        return null;
      }

      const timestamp = typeof parsed.timestamp === 'number' ? parsed.timestamp : 0;

      if (timestamp && Date.now() - timestamp > STATE_TTL_MS) {
        this.clearState();
        return null;
      }

      const htmlStorageKey = getHtmlStorageKey(this.storageKey);
      let html = readHtmlFromStorage(htmlStorageKey);

      // Backwards compatibility: if the stored payload still contains HTML inline,
      // migrate it into the dedicated HTML key.
      if (typeof parsed.html === 'string' && !html) {
        html = parsed.html;

        try {
          writeHtmlToStorage(htmlStorageKey, html);

          const metaOnlyState = Object.assign({}, parsed);
          delete metaOnlyState.html;

          window.sessionStorage.setItem(this.storageKey, JSON.stringify(metaOnlyState));
          parsed = metaOnlyState;
        } catch (error) {
          // Ignore migration errors; we'll still use the in-memory HTML if present.
        }
      }

      if (typeof html !== 'string' || html === '') {
        this.clearState();
        return null;
      }

      const state = Object.assign({}, parsed, { html });

      this.state = state;

      return state;
    }

    saveState() {
      if (!supportsSessionStorage) return;

      // Ensure we are always saving under a key that matches the current URL,
      // so filtered/sorted states do not overwrite or get mixed with unfiltered state.
      updateStorageKeyFromURL(this);

      const doSave = () => {
        const grid = document.getElementById('product-grid');
        if (!grid) return;

        // Clone grid and strip out transient skeleton/loading items so the
        // cached HTML never depends on JS-driven fetches to complete.
        const gridClone = grid.cloneNode(true);
        const skeletonItems = gridClone.querySelectorAll(
          '.grid__item--skeleton-loadmore, .grid__item--loader'
        );

        skeletonItems.forEach((node) => {
          if (node.parentNode) {
            node.parentNode.removeChild(node);
          }
        });

        const html = gridClone.innerHTML;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const pages = computeLoadedPagesFromDOM();

        let lowestPage = null;
        let highestPage = null;

        if (pages.size > 0) {
          const sorted = Array.from(pages).sort((a, b) => a - b);
          lowestPage = sorted[0];
          highestPage = sorted[sorted.length - 1];
        }

        const timestamp = Date.now();

        const metaState = {
          scrollY,
          lowestPage,
          highestPage,
          timestamp,
          url: window.location.pathname + window.location.search
        };

        const state = Object.assign({ html }, metaState);

        this.state = state;

        const htmlStorageKey = getHtmlStorageKey(this.storageKey);

        try {
          const serializedMeta = JSON.stringify(metaState);
          window.sessionStorage.setItem(this.storageKey, serializedMeta);
          writeHtmlToStorage(htmlStorageKey, html);
        } catch (error) {
          // Swallow storage quota or serialization errors silently
        }
      };

      // For correctness, save immediately on every call. This keeps
      // scrollY and highestPage accurate even during rapid infinite-scroll.
      doSave();
    }

    clearState() {
      if (!supportsSessionStorage) return;

      try {
        const htmlStorageKey = getHtmlStorageKey(this.storageKey);

        window.sessionStorage.removeItem(this.storageKey);
        window.sessionStorage.removeItem(htmlStorageKey);
      } catch (error) {
        // Ignore
      }

      this.state = null;
    }

    restoreFromState(state) {
      const container = document.getElementById('ProductGridContainer');
      const grid = document.getElementById('product-grid');

      if (!container || !grid || !state || typeof state.html !== 'string') return;

      grid.innerHTML = state.html;

      // Primary scroll restore is left to the browser's own history/back behavior.
      // However, on iOS we force manual restoration to ensure the "instant flick"
      // behavior works reliably, especially for text link returns.
      if (isIos && typeof state.scrollY === 'number' && state.scrollY > 0) {
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, state.scrollY);
      }

      // Sync the scroll-to-product-grid component and its page tracking
      this.syncScrollComponentWithDOMPages();
    }

    setupMutationObserver(container) {
      if (!('MutationObserver' in window) || !container) return;

      this.mutationObserver = new MutationObserver((mutations) => {
        let shouldSave = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            shouldSave = true;
            break;
          }
        }

        if (shouldSave) {
          this.saveState();
        }
      });

      this.mutationObserver.observe(container, {
        childList: true,
        subtree: true
      });
    }

    setupProductClickHandler() {
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      grid.addEventListener('click', (event) => {
        let target = event.target;
        if (!target) return;

        // Handle text nodes which can be the target on iOS when clicking text
        // This was the critical fix for iOS text links
        if (target.nodeType === 3) {
          target = target.parentNode;
        }

        if (!(target instanceof Element)) return;

        const link = target.closest('a[href]');
        if (!link) return;

        // Only intercept normal primary-button clicks without modifier keys.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }

        const href = link.getAttribute('href');
        if (!href) return;

        let url;

        try {
          url = new URL(href, window.location.origin);
        } catch (error) {
          return;
        }

        if (!url.pathname.includes('/products/')) return;

        // Prevent the browser's default navigation so we can ensure our
        // cached state (including scroll position) is saved first, then
        // navigate programmatically. This avoids subtle timing differences
        // between different links (e.g. image vs text) and across browsers.
        event.preventDefault();

        this.isNavigatingToProduct = true;
        this.saveState();

        window.location.href = url.pathname + url.search + url.hash;
      });
    }

    setupLoadMoreListener() {
      // When load-more or load-prev updates the URL, also capture state
      document.addEventListener('loadmore:url-updated', () => {
        this.saveState();
      });
    }

    setupUnloadListeners() {
      if (typeof window === 'undefined') return;

      // BFCache-friendly: listen for pagehide and visibilitychange (avoid beforeunload)
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.handlePageHide();
      });
    }

    handlePageHide() {
      if (this.isNavigatingToProduct) return;
      this.saveState();
    }

    async preloadPreviousPagesForInitialPage(currentPage) {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;

      const grid = container.querySelector('.product-grid');
      if (!grid) return;

      // Scroll to the first item of the current page as early as possible
      const currentPageItemInitial = findFirstItemForPage(currentPage);
      if (currentPageItemInitial) {
        scrollToElement(currentPageItemInitial);
      }

      // Collect items from all previous pages in parallel, preserving page order
      const allItems = [];
      const pageItemsMap = new Map();
      const fetchPromises = [];

      for (let page = 1; page < currentPage; page += 1) {
        const url = buildPageURL(page);

        const fetchPromise = fetch(url)
          .then((response) => {
            if (!response || !response.ok) return null;
            return response.text();
          })
          .then((htmlText) => {
            if (!htmlText) return;
            const parsed = parseGridFromHTML(htmlText);
            const items = parsed.items || [];
            if (items.length > 0) {
              pageItemsMap.set(page, items);
            }
          })
          .catch(() => {
            // Ignore failures for individual pages
          });

        fetchPromises.push(fetchPromise);
      }

      if (fetchPromises.length === 0) return;

      await Promise.all(fetchPromises);

      for (let page = 1; page < currentPage; page += 1) {
        const items = pageItemsMap.get(page);
        if (!items || !items.length) continue;

        for (const item of items) {
          allItems.push(item);
        }
      }

      if (allItems.length === 0) return;

      const fragment = document.createDocumentFragment();

      for (const item of allItems) {
        fragment.appendChild(item);
      }

      // Track the current page item as an anchor so we can keep it in view
      const anchorItem = findFirstItemForPage(currentPage);
      let anchorTopBefore = null;

      if (anchorItem) {
        const rectBefore = anchorItem.getBoundingClientRect();
        anchorTopBefore = rectBefore.top + window.pageYOffset;
      }

      // Insert before the first real product item, but after any card-description block
      let insertBeforeNode = null;
      const children = Array.from(grid.children);

      for (const child of children) {
        if (child.classList && child.classList.contains('card-description')) {
          // Skip the description card
          continue;
        }

        insertBeforeNode = child;
        break;
      }

      if (insertBeforeNode) {
        grid.insertBefore(fragment, insertBeforeNode);
      } else {
        grid.appendChild(fragment);
      }

      // After inserting earlier pages, keep the current page item stable in the viewport
      if (anchorItem && anchorTopBefore !== null) {
        const rectAfter = anchorItem.getBoundingClientRect();
        const anchorTopAfter = rectAfter.top + window.pageYOffset;
        const delta = anchorTopAfter - anchorTopBefore;

        if (Math.abs(delta) > 1) {
          window.scrollTo({
            top: window.pageYOffset + delta,
            left: 0,
            behavior: 'auto'
          });
        }
      } else {
        const currentPageItem = findFirstItemForPage(currentPage);
        if (currentPageItem) {
          scrollToElement(currentPageItem);
        }
      }

      // Sync components and persist this hydrated state
      this.syncScrollComponentWithDOMPages();
      this.saveState();
    }

    syncScrollComponentWithDOMPages() {
      if (!('customElements' in window)) return;

      const pages = computeLoadedPagesFromDOM();
      if (pages.size === 0) return;

      const pagesArray = Array.from(pages);

      // Also align the load-more button so it only ever fetches pages
      // beyond the highest one we already have in the DOM.
      this.syncLoadMoreWithLoadedPages(pagesArray);

      // Sync scroll-to-product-grid
      if (customElements.whenDefined) {
        customElements.whenDefined('scroll-to-product-grid').then(() => {
          const scrollComponent = document.querySelector('scroll-to-product-grid');
          if (!scrollComponent) return;

          // Replace the component's loadedPages with the set derived from DOM
          if (scrollComponent.loadedPages && scrollComponent.loadedPages instanceof Set) {
            scrollComponent.loadedPages.clear();
            pagesArray.forEach((page) => scrollComponent.loadedPages.add(page));
          } else {
            scrollComponent.loadedPages = new Set(pagesArray);
          }

          if (typeof scrollComponent.initializeProductBaseline === 'function') {
            scrollComponent.initializeProductBaseline();
          }

          if (typeof scrollComponent.observeProducts === 'function') {
            scrollComponent.observeProducts();
          }
        });
      }

      // Also sync load-prev-pages so its visibility and links match the hydrated pages
      if (customElements.whenDefined) {
        customElements.whenDefined('load-prev-pages').then(() => {
          const loadPrevElement = document.querySelector('load-prev-pages');
          if (!loadPrevElement) return;

          if (loadPrevElement.loadedPages && loadPrevElement.loadedPages instanceof Set) {
            loadPrevElement.loadedPages.clear();
            pagesArray.forEach((page) => loadPrevElement.loadedPages.add(page));
          } else {
            loadPrevElement.loadedPages = new Set(pagesArray);
          }

          if (typeof loadPrevElement.updateVisibility === 'function') {
            loadPrevElement.updateVisibility();
          }

          if (typeof loadPrevElement.updateButtonHref === 'function') {
            loadPrevElement.updateButtonHref();
          }
        });
      }
    }

    /**
     * Ensure load-more only fetches pages beyond the highest one we have.
     * This prevents refetching pages that were restored from cache.
     *
     * @param {number[]} pagesArray - Sorted list of loaded page numbers.
     */
    syncLoadMoreWithLoadedPages(pagesArray) {
      if (!Array.isArray(pagesArray) || pagesArray.length === 0) return;

      const highestPage = Math.max.apply(null, pagesArray);
      if (!Number.isFinite(highestPage) || highestPage < 1) return;

      const loadMoreEl = document.querySelector('load-more-button');
      if (!loadMoreEl) return;

      const btn = loadMoreEl.querySelector('a[role="button"]');
      if (!btn) return;

      const totalPagesInput = btn.querySelector('[data-total-pages]');
      const currentPageInput = btn.querySelector('[data-current-page]');
      const nextUrlInput = btn.querySelector('[data-next-url]');

      if (!totalPagesInput || !currentPageInput || !nextUrlInput) return;

      const totalPages = parseInt(totalPagesInput.value, 10) || highestPage;
      const clampedHighest = Math.min(highestPage, totalPages);

      // Update current page to reflect what we actually have loaded
      currentPageInput.value = String(clampedHighest);

      // If we've already loaded the final page, hide the button
      if (clampedHighest >= totalPages) {
        loadMoreEl.classList.add('hidden');
        return;
      }

      // Otherwise, point the next URL at the first page we don't already have
      const nextPage = clampedHighest + 1;
      const nextUrl = buildPageURL(nextPage);

      btn.setAttribute('href', nextUrl);
      nextUrlInput.value = nextUrl;
    }
  }

  // Initialize on DOMContentLoaded to ensure #ProductGridContainer is present
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductGridCacheManager();
    });
  } else {
    new ProductGridCacheManager();
  }
})();
